import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
} from '@mui/material';
import Card from './Card';
import EmptyState from './EmptyState';
import Loading from './Loading';
import StatusBadge, { SkippedBadge, AutoApprovalBadge, statusLabel } from './StatusBadge';
import { jobDisplayStatus, outOfScopeReason } from '../utils/healOutcome';
import RejectDialog from './RejectDialog';
import { skippedGateCode } from '../utils/healingGate';
import ReviewPanel from './ReviewPanel';
import RevisionChatDialog from './RevisionChatDialog';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import MultiSelectFilter from './MultiSelectFilter';
import { filterJobs, hasActiveFilters as anyFilterActive } from '../utils/jobFilters';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useHealingJobs, JOBS_PAGE_SIZE } from '../contexts/HealingJobContext';
import api from '../services/api';

/* ================================================================== */
/*  Job Row — compact card layout per job                              */
/* ================================================================== */

function JobRow({ job, isExpanded, onToggle, onPollJob, isPolling, onApprove, onReject, onRevise, onReopen, actionLoading, githubConfigured, onGoToIssue }) {
  const canExpand = !!job.review_context;
  const oosReason = outOfScopeReason(job);
  const rowMessage = oosReason !== null ? `Out of scope: ${oosReason}` : job.message;
  return (
    <Box sx={{ '&:not(:last-child)': { borderBottom: '1px solid rgba(255,255,255,0.04)' } }}>
      {/* Main row */}
      <Box
        onClick={() => canExpand && onToggle(job.id)}
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 2,
          px: 3,
          py: 2,
          cursor: canExpand ? 'pointer' : 'default',
          transition: 'background 0.15s',
          background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent',
          '&:hover': { background: 'rgba(255,255,255,0.02)' },
        }}
      >
        {/* Left: primary info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.9rem' }}>
              {job.retailer || 'N/A'}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>
              {job.attribute || 'N/A'}
            </Typography>
            <Chip
              label={job.page_type || 'detail'}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.68rem', borderColor: 'rgba(148,163,184,0.35)', color: '#94a3b8' }}
            />
            <Chip
              label={(job.issue_type || 'null-issues') === 'non-null-issues' ? 'Non-Null' : 'Null'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 600,
                background: (job.issue_type || 'null-issues') === 'non-null-issues'
                  ? 'rgba(245, 158, 11, 0.15)'
                  : 'rgba(59, 130, 246, 0.15)',
                color: (job.issue_type || 'null-issues') === 'non-null-issues' ? '#f59e0b' : '#60a5fa',
                border: (job.issue_type || 'null-issues') === 'non-null-issues'
                  ? '1px solid rgba(245, 158, 11, 0.3)'
                  : '1px solid rgba(59, 130, 246, 0.3)',
              }}
            />
            {skippedGateCode(job)
              ? <SkippedBadge code={skippedGateCode(job)} />
              : <StatusBadge status={jobDisplayStatus(job)} title={oosReason !== null ? `Out of scope: ${oosReason}` : undefined} />}
            <AutoApprovalBadge job={job} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#475569', fontSize: '0.75rem', fontFamily: 'monospace' }}>
              {job.id}
            </Typography>
            {rowMessage && (
              <Typography
                sx={{ color: '#64748b', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}
                title={rowMessage}
              >
                {rowMessage}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: meta + actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
            <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
              {job.triggered_by || '—'}
            </Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.7rem' }}>
              {new Date(job.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Refresh status">
              <IconButton
                size="small"
                disabled={isPolling}
                onClick={() => onPollJob(job.id)}
                sx={{
                  color: '#64748b',
                  '&:hover': { color: '#94a3b8', background: 'rgba(255,255,255,0.05)' },
                }}
              >
                <SyncIcon
                  sx={{
                    fontSize: 18,
                    animation: isPolling ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={job.anomaly_id ? 'Take me to the issue' : 'No linked anomaly for this job'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!job.anomaly_id}
                  onClick={() => onGoToIssue(job.anomaly_id)}
                  sx={{
                    color: '#64748b',
                    '&:hover': { color: '#94a3b8', background: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            {canExpand && (
              <Tooltip title={isExpanded ? 'Hide details' : 'View details'}>
                <IconButton
                  size="small"
                  onClick={() => onToggle(job.id)}
                  sx={{
                    color: '#64748b',
                    '&:hover': { color: '#94a3b8', background: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* Expanded review panel */}
      <Collapse in={isExpanded}>
        {isExpanded && job.review_context && (
          <ReviewPanel
            job={job}
            onApprove={onApprove}
            onReject={onReject}
            onRevise={onRevise}
            onReopen={onReopen}
            actionLoading={actionLoading}
            githubConfigured={githubConfigured}
          />
        )}
      </Collapse>
    </Box>
  );
}

/* ================================================================== */
/*  (ReportGroup removed — jobs now displayed at anomaly level)         */
/* ================================================================== */

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const EMPTY_JOB_FILTERS = { status: '', retailer: [], attribute: [], triggered_by: '', page_type: '', issue_type: '', created_from: '', created_to: '' };

function JobTrackerTab() {
  const {
    jobs, total, hasMore, loading, loadingMore, error,
    refreshJobs, setDateRange, loadMore, pollJob, approveJob, rejectJob, reopenJob,
  } = useHealingJobs();
  const [pollingJobs, setPollingJobs] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [githubConfigured, setGithubConfigured] = useState(true);
  const [expandedJobs, setExpandedJobs] = useState(new Set());

  // Filters
  const [filters, setFilters] = useState(EMPTY_JOB_FILTERS);

  // Dialogs
  const [rejectDialog, setRejectDialog] = useState({ open: false, jobId: null });
  const [chatDialog, setChatDialog] = useState({ open: false, job: null });

  // Persistent chat history cache — survives dialog close/reopen
  const chatCacheRef = useRef({});

  useEffect(() => {
    api.getGithubCredentialStatus()
      .then((data) => setGithubConfigured(data.configured))
      .catch(() => setGithubConfigured(false));
  }, []);

  // Created from/to is filtered on the server: (re)load the first page on mount
  // and whenever the range changes. The context clears the list for an inverted range.
  const { created_from: createdFrom, created_to: createdTo } = filters;
  useEffect(() => {
    setDateRange({ createdFrom, createdTo });
  }, [createdFrom, createdTo, setDateRange]);

  const handleGoToIssue = useCallback((anomalyId) => {
    if (!anomalyId) return;
    window.open('/anomalies?anomaly=' + anomalyId, '_blank', 'noopener,noreferrer');
  }, []);

  // Most-recent-first list (the backend already returns jobs sorted this way;
  // sort defensively in case of client-side updates from poll/approve/reject).
  const allJobs = useMemo(() => {
    return [...jobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [jobs]);

  // Derive filter options from jobs
  const filterOptions = useMemo(() => {
    const retailers = [...new Set(allJobs.map((j) => j.retailer).filter(Boolean))].sort();
    const attributes = [...new Set(allJobs.map((j) => j.attribute).filter(Boolean))].sort();
    const statuses = [...new Set(allJobs.map(jobDisplayStatus).filter(Boolean))].sort();
    const users = [...new Set(allJobs.map((j) => j.triggered_by).filter(Boolean))].sort();
    const pageTypes = [...new Set(allJobs.map((j) => j.page_type || 'detail').filter(Boolean))].sort();
    const issueTypes = [...new Set(allJobs.map((j) => j.issue_type || 'null-issues').filter(Boolean))].sort();
    return { retailers, attributes, statuses, users, pageTypes, issueTypes };
  }, [allJobs]);

  // Apply the other filters client-side to the loaded jobs. The date range was
  // already applied by the server (UTC days), so it is not re-applied here in
  // local time, which could hide jobs the server returned.
  const filteredJobs = useMemo(() => {
    const { created_from: _from, created_to: _to, ...clientFilters } = filters;
    return filterJobs(allJobs, clientFilters);
  }, [allJobs, filters]);

  const hasActiveFilters = anyFilterActive(filters);
  const clearFilters = () => setFilters(EMPTY_JOB_FILTERS);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const toggleJobExpand = (jobId) => {
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  /* Poll */
  const handlePollJob = useCallback(async (jobId) => {
    setPollingJobs((prev) => new Set(prev).add(jobId));
    try {
      await pollJob(jobId);
    } catch (err) {
      showSnackbar(`Failed to poll: ${err.message}`, 'error');
    } finally {
      setPollingJobs((prev) => { const n = new Set(prev); n.delete(jobId); return n; });
    }
  }, [pollJob]);

  /* Approve */
  const handleApprove = useCallback(async (
    jobId,
    editedConfig = null,
    editedParsedConfig = null,
  ) => {
    setActionLoading(true);
    try {
      const data = await approveJob(
        jobId,
        'DTP-100',
        editedConfig,
        editedParsedConfig,
      );
      showSnackbar(`Job approved${data.pr_url ? ` — PR: ${data.pr_url}` : ''}`);
    } catch (err) {
      showSnackbar(`Approve failed: ${err.message}`, 'error');
    } finally {
      setActionLoading(false);
    }
  }, [approveJob]);

  /* Reject */
  const openRejectDialog = (jobId) => { setRejectDialog({ open: true, jobId }); };
  const handleRejectConfirm = useCallback(async (reason, reasonCode) => {
    if (!reason || !reason.trim()) return;
    setActionLoading(true);
    try {
      await rejectJob(rejectDialog.jobId, reason.trim(), reasonCode);
      showSnackbar('Job rejected');
      setRejectDialog({ open: false, jobId: null });
    } catch (err) {
      showSnackbar(`Reject failed: ${err.message}`, 'error');
    } finally {
      setActionLoading(false);
    }
  }, [rejectJob, rejectDialog.jobId]);

  /* Reopen — errors are re-thrown so ReviewPanel can show the agent's message inline. */
  const handleReopen = useCallback(async (jobId) => {
    setActionLoading(true);
    try {
      const data = await reopenJob(jobId);
      showSnackbar('Job reopened for review');
      return data;
    } finally {
      setActionLoading(false);
    }
  }, [reopenJob]);

  /* Revise (chat-based) */
  const openChatDialog = useCallback((jobId) => {
    const found = allJobs.find((j) => j.id === jobId);
    if (found) {
      setChatDialog({ open: true, job: found });
    }
  }, [allJobs]);

  const handleRevisionComplete = useCallback(async (jobId) => {
    try {
      await pollJob(jobId);
    } catch {
      // Silently ignore — user can manually refresh
    }
  }, [pollJob]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h5" sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: '1.25rem' }}>
              Healing Jobs
            </Typography>
            {jobs.length > 0 && (
              <Typography sx={{ color: '#475569', fontSize: '0.85rem' }}>
                Showing {jobs.length}{total != null ? ` of ${total}` : ''} job{(total ?? jobs.length) !== 1 ? 's' : ''}
                {filteredJobs.length !== jobs.length ? ` · ${filteredJobs.length} match filters` : ''}
              </Typography>
            )}
          </Box>
          <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
            Created from/to searches all jobs (UTC days). Other filters apply to loaded jobs; use Load more to search further back.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => refreshJobs()}
            disabled={loading}
            size="small"
            sx={{
              color: '#64748b',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 1.5,
              '&:hover': { background: 'rgba(255,255,255,0.05)', color: '#94a3b8' },
            }}
          >
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterListIcon sx={{ color: '#64748b' }} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status" onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              {filterOptions.statuses.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Retailer</InputLabel>
            <MultiSelectFilter label="Retailer" value={filters.retailer} options={filterOptions.retailers} onChange={(e) => setFilters((f) => ({ ...f, retailer: e.target.value }))} />
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Attribute</InputLabel>
            <MultiSelectFilter label="Attribute" value={filters.attribute} options={filterOptions.attributes} onChange={(e) => setFilters((f) => ({ ...f, attribute: e.target.value }))} />
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Page Type</InputLabel>
            <Select value={filters.page_type} label="Page Type" onChange={(e) => setFilters((f) => ({ ...f, page_type: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              {filterOptions.pageTypes.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Issue Type</InputLabel>
            <Select value={filters.issue_type} label="Issue Type" onChange={(e) => setFilters((f) => ({ ...f, issue_type: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              {filterOptions.issueTypes.map((t) => (
                <MenuItem key={t} value={t}>{t === 'non-null-issues' ? 'Non-Null' : t === 'null-issues' ? 'Null' : t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Triggered By</InputLabel>
            <Select value={filters.triggered_by} label="Triggered By" onChange={(e) => setFilters((f) => ({ ...f, triggered_by: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              {filterOptions.users.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="Created from (UTC)"
            value={filters.created_from}
            onChange={(e) => setFilters((f) => ({ ...f, created_from: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: filters.created_to || undefined, 'aria-label': 'Created from (UTC)' }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label="Created to (UTC)"
            value={filters.created_to}
            onChange={(e) => setFilters((f) => ({ ...f, created_to: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: filters.created_from || undefined, 'aria-label': 'Created to (UTC)' }}
            sx={{ width: 160 }}
          />
          {hasActiveFilters && (
            <Tooltip title="Clear filters">
              <IconButton size="small" onClick={clearFilters}><ClearIcon sx={{ color: '#64748b' }} /></IconButton>
            </Tooltip>
          )}
        </Box>
      </Card>

      {/* GitHub credentials banner */}
      {!githubConfigured && (
        <Alert
          severity="warning"
          sx={{ mb: 2, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}
          action={
            <Button color="inherit" size="small" href="/settings" sx={{ textTransform: 'none', fontWeight: 600 }}>
              Go to Settings
            </Button>
          }
        >
          GitHub credentials not configured. You will not be able to approve healing jobs until you add your GitHub PAT in Settings.
        </Alert>
      )}

      {/* Load error (refresh, date range or Load more); cleared by the next successful load */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {loading && allJobs.length === 0 ? (
        <Loading message="Loading healing jobs..." />
      ) : filteredJobs.length === 0 ? (
        <Card>
          <EmptyState message={hasActiveFilters ? "No jobs match the current filters." : "No healing jobs yet. Trigger one from the Anomalies tab."} />
        </Card>
      ) : (
        <Card sx={{ overflow: 'hidden' }}>
          {filteredJobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              isExpanded={expandedJobs.has(job.id)}
              onToggle={toggleJobExpand}
              onPollJob={handlePollJob}
              isPolling={pollingJobs.has(job.id)}
              onApprove={handleApprove}
              onReject={openRejectDialog}
              onRevise={openChatDialog}
              onReopen={handleReopen}
              actionLoading={actionLoading}
              githubConfigured={githubConfigured}
              onGoToIssue={handleGoToIssue}
            />
          ))}
        </Card>
      )}

      {hasMore && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => loadMore()}
            disabled={loadingMore || loading}
            sx={{ textTransform: 'none' }}
          >
            {loadingMore ? 'Loading…' : `Load more (next ${JOBS_PAGE_SIZE})`}
          </Button>
        </Box>
      )}

      {/* Reject Dialog */}
      <RejectDialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, jobId: null })}
        onConfirm={handleRejectConfirm}
        actionLoading={actionLoading}
      />

      {/* Revision Chat Dialog */}
      <RevisionChatDialog
        open={chatDialog.open}
        onClose={() => setChatDialog({ open: false, job: null })}
        job={chatDialog.job}
        onRevisionComplete={handleRevisionComplete}
        chatCacheRef={chatCacheRef}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default JobTrackerTab;
